import { z } from 'zod';
import { ruleFor, type WelcomeCareCallType } from '../care/care-call-types';
import type { CareLicenceReadiness } from '../care/care-call-types';
import type { ProgramConfig } from './schema';

/**
 * Whether medication is part of care for any enrolled programme.
 *
 * Two consumers, deliberately sharing one predicate: staff-initiated
 * provider-call eligibility on the server, and the member-facing medication
 * card on mobile. Both ask the same question, so they move together — a second
 * field for the second consumer would be the drift this repo keeps paying for.
 * The name of the flag, not of either surface, is what this answers.
 */
export function medicationEligibleFromProgramConfigs(configs: readonly ProgramConfig[]): boolean {
  return configs.some((config) => config.care?.medication_eligible === true);
}

function configuredWelcomeCallType(config: ProgramConfig): WelcomeCareCallType {
  const persisted = String(config.care?.welcome_call_type ?? 'welcome_call_substance');
  if (persisted === 'welcome_call_substance' || persisted === 'welcome_call_sud') {
    return 'welcome_call_substance';
  }
  if (persisted === 'welcome_call_tobacco') {
    return 'welcome_call_tobacco';
  }
  if (persisted === 'welcome_call_behavioral') {
    return 'welcome_call_behavioral';
  }
  return 'welcome_call_substance';
}

/**
 * Member welcome call type from enrolled programmes. Substance path wins when
 * co-enrolled; among intake-only types tobacco wins over behavioural.
 */
export function welcomeCallTypeFromProgramConfigs(
  configs: readonly ProgramConfig[],
): WelcomeCareCallType {
  return welcomeCallTypeFromProgrammeWelcomeTypes(
    configs.map((config) => configuredWelcomeCallType(config)),
  );
}

const storedWelcomeCallTypeSchema = z
  .object({ care: z.object({ welcome_call_type: z.unknown() }).partial() })
  .partial();

/**
 * A programme's welcome call type read straight from its stored config, for a
 * config the read schema refuses: tobacco and behavioural as named, anything
 * else substance. This is how the legacy-import repair migration reads the same
 * JSON, so the importer and the migration agree on a malformed config; for a
 * config that parses it equals the parsed config's welcome type.
 */
export function welcomeCallTypeFromStoredProgrammeConfig(config: unknown): WelcomeCareCallType {
  const stored = storedWelcomeCallTypeSchema.safeParse(config);
  const value = stored.success ? stored.data.care?.welcome_call_type : undefined;
  return value === 'welcome_call_tobacco' || value === 'welcome_call_behavioral'
    ? value
    : 'welcome_call_substance';
}

/** The member's welcome call type from each enrolled programme's own welcome type. */
export function welcomeCallTypeFromProgrammeWelcomeTypes(
  types: readonly WelcomeCareCallType[],
): WelcomeCareCallType {
  if (types.some((type) => type === 'welcome_call_substance')) {
    return 'welcome_call_substance';
  }
  if (types.some((type) => type === 'welcome_call_tobacco')) {
    return 'welcome_call_tobacco';
  }
  if (types.some((type) => type === 'welcome_call_behavioral')) {
    return 'welcome_call_behavioral';
  }
  return 'welcome_call_substance';
}

/** Licence readiness gate from the main programme, falling back to the first enrollment. */
export function licenceReadinessFromEnrolledPrograms(
  rows: readonly { config: ProgramConfig; mainProgram: boolean }[],
): CareLicenceReadiness | null {
  const main = rows.find((row) => row.mainProgram)?.config ?? rows[0]?.config;
  return main?.care?.licence_readiness ?? null;
}

/** Whether a completed welcome call should advance the member to treatment_plan. */
export function followedByTreatmentPlanFromEnrolledPrograms(
  rows: readonly { config: ProgramConfig; mainProgram: boolean }[],
): boolean {
  if (rows.length === 0) {
    return ruleFor('welcome_call_substance').followedByTreatmentPlan;
  }
  const main = rows.find((row) => row.mainProgram)?.config ?? rows[0]?.config;
  const explicit = main?.care?.followed_by_treatment_plan;
  if (explicit !== undefined) return explicit;
  const welcomeType = welcomeCallTypeFromProgramConfigs(rows.map((row) => row.config));
  return ruleFor(welcomeType).followedByTreatmentPlan;
}
